import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // null = checking, false = not auth, object = user
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        // Skip the auth probe entirely for anonymous visitors. On public
        // pages the `/auth/me` endpoint would otherwise return 401 and
        // surface a red network entry in the browser console, which is
        // an institutionally-visible technical imperfection. Since the
        // access token is stored in localStorage on login, absence of
        // the token is a definitive signal that the visitor is
        // unauthenticated and no server call is required.
        const hasToken =
            typeof window !== "undefined" &&
            !!window.localStorage.getItem("crewz_token");
        if (!hasToken) {
            setUser(false);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (_e) {
            // Token expired or invalid — clear it silently and become
            // anonymous. No console noise for the visitor.
            if (typeof window !== "undefined") {
                window.localStorage.removeItem("crewz_token");
            }
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.access_token) {
            window.localStorage.setItem("crewz_token", data.access_token);
        }
        setUser(data.user);
        return data.user;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        if (data.access_token) {
            window.localStorage.setItem("crewz_token", data.access_token);
        }
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (_e) {
            // ignore
        }
        window.localStorage.removeItem("crewz_token");
        setUser(false);
    };

    return (
        <AuthContext.Provider
            value={{ user, loading, login, register, logout, refresh, formatApiErrorDetail }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
